<template>
  <div 
    v-show="visible && !isMinimized" 
    class="draggable-panel-container" 
    :style="panelStyle"
    ref="panelContainer"
    @mousedown="handlePanelFocus"
  >
    <!-- 头部区域 - 可拖拽 -->
    <div 
      class="draggable-panel-head" 
      ref="panelHead"
    >
      <span class="draggable-panel-title">
        <i v-if="icon" :class="icon"></i>
        {{ title }}
      </span>
      <div class="draggable-panel-nav">
        <!-- 导航标签页 -->
        <span 
          v-for="tab in tabs" 
          :key="tab.key"
          class="nav-item" 
          :class="{ active: activeTab === tab.key }"
          @click="handleTabClick(tab.key)"
        >
          {{ tab.label }}
          <span v-if="tab.badge" class="badge">{{ tab.badge }}</span>
        </span>
        
        <!-- 展开/收起按钮 -->
        <span 
          v-if="enableExpand" 
          class="draggable-panel-icon" 
          :title="isExpanded ? '收起' : '展开'" 
          @click.stop="handleExpandToggle"
        >
          <i :class="isExpanded ? 'el-icon-d-arrow-left' : 'el-icon-d-arrow-right'"></i>
        </span>
        
        <!-- 最大化/还原按钮 -->
        <span
          v-if="enableMaximize"
          class="draggable-panel-icon"
          :title="isMaximized ? '还原' : '最大化'"
          @click.stop="handleMaximizeToggle"
        >
          <i :class="isMaximized ? 'el-icon-c-scale-to-original' : 'el-icon-full-screen'"></i>
        </span>

        <!-- 最小化按钮 -->
        <span 
          v-if="enableMinimize"
          class="draggable-panel-icon" 
          title="最小化" 
          @click.stop="handleMinimize"
        >
          <i class="el-icon-arrow-down"></i>
        </span>
        
        <!-- 关闭按钮 -->
        <span class="draggable-panel-icon" title="关闭" @click.stop="handleClose">
          <i class="el-icon-close"></i>
        </span>
      </div>
    </div>

    <!-- 内容区域 -->
    <div class="draggable-panel-content">
      <slot></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DraggablePanel',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: '面板标题'
    },
    icon: {
      type: String,
      default: ''
    },
    width: {
      type: [String, Number],
      default: 950
    },
    height: {
      type: [String, Number],
      default: 'auto'
    },
    initialPosition: {
      type: Object,
      default: () => ({
        top: 100,
        right: 400
      })
    },
    tabs: {
      type: Array,
      default: () => []
    },
    activeTab: {
      type: String,
      default: ''
    },
    zIndex: {
      type: Number,
      default: 1000
    },
    // 展开/收起功能相关 props
    enableExpand: {
      type: Boolean,
      default: false
    },
    isExpanded: {
      type: Boolean,
      default: false
    },
    expandedWidth: {
      type: [String, Number],
      default: 1200
    },
    // 最大化/还原
    enableMaximize: {
      type: Boolean,
      default: false
    },
    // 是否显示最小化按钮
    enableMinimize: {
      type: Boolean,
      default: true
    },
    // 面板唯一标识，用于最小化功能
    panelId: {
      type: String,
      default: null
    }
  },
  inject: {
    registerMinimizedPanel: {
      from: 'registerMinimizedPanel',
      default: null
    },
    unregisterMinimizedPanel: {
      from: 'unregisterMinimizedPanel',
      default: null
    }
  },
  data() {
    return {
      interactInstance: null,
      // 位置状态管理 - 使用transform坐标
      position: {
        x: 0,
        y: 0
      },
      isInitialized: false,
      // 导航栏状态监听
      sidebarCollapsed: false,
      // 轮询定时器
      sidebarCheckTimer: null,
      // 最小化状态
      isMinimized: false,
      isMaximized: false,
      // 面板唯一标识（自动生成）
      internalPanelId: null
    }
  },
  computed: {
    panelStyle() {
      // 根据展开状态决定宽度
      let currentWidth = this.width
      if (this.enableExpand && this.isExpanded) {
        currentWidth = this.expandedWidth
      }
      const width = typeof currentWidth === 'number' ? `${currentWidth}px` : currentWidth
      const height = typeof this.height === 'number' ? `${this.height}px` : this.height
      
      const style = {
        width,
        height: height !== 'auto' ? height : undefined,
        zIndex: this.zIndex,
        // 使用transform进行定位，避免与interact.js冲突
        transform: `translate(${this.position.x}px, ${this.position.y}px)`
      }
      

      // 最大化：填满内容区（避开左侧导航与顶部工具栏）
      if (this.isMaximized) {
        style.width = 'calc(100vw - 130px)'
        style.height = 'calc(100vh - 130px)'
        style.transform = `translate(${this.getSidebarWidth() + 8}px, 60px)`
      }

      return style
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        // 更新导航栏状态（每次显示时都检查）
        this.updateSidebarState()
        
        this.$nextTick(() => {
          // 初始化位置（只在第一次显示时）
          if (!this.isInitialized) {
            this.initializePosition()
            this.isInitialized = true
          } else {
            // 如果已经初始化，但用户没有手动设置 left，则根据导航栏状态更新位置
            if (this.initialPosition.left === undefined) {
              this.updatePositionForSidebar()
            }
          }
          
          if (!this.interactInstance) {
            this.initInteract()
          }
        })
        
        // 启动轮询检查导航栏状态（仅在面板可见时）
        this.startSidebarCheck()
      } else {
        // 面板隐藏时停止轮询
        this.stopSidebarCheck()
      }
    },
    
    // 监听初始位置变化，重新计算transform位置
    initialPosition: {
      handler() {
        if (this.visible && !this.isInitialized) {
          this.initializePosition()
        }
      },
      deep: true
    }
  },
  mounted() {
    // 生成面板唯一标识
    this.internalPanelId = this.panelId || `panel_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    
    // 初始化导航栏状态
    this.updateSidebarState()
    // 监听 localStorage 变化（导航栏状态变化，用于跨窗口同步）
    window.addEventListener('storage', this.handleStorageChange)
    // 如果组件挂载时就是可见的，初始化位置
    if (this.visible && !this.isInitialized) {
      this.initializePosition()
      this.isInitialized = true
      // 启动轮询检查
      this.startSidebarCheck()
    }
    this.initInteract()
  },
  beforeDestroy() {
    // 移除事件监听
    window.removeEventListener('storage', this.handleStorageChange)
    // 停止轮询
    this.stopSidebarCheck()
    // 注销最小化面板
    if (this.isMinimized && this.unregisterMinimizedPanel) {
      this.unregisterMinimizedPanel(this.internalPanelId)
    }
    if (this.interactInstance) {
      this.interactInstance.unset()
    }
  },
  methods: {
    handleClose() {
      this.$emit('close')
      this.$emit('update:visible', false)
    },
    
    handleTabClick(tabKey) {
      this.$emit('tab-change', tabKey)
      this.$emit('update:activeTab', tabKey)
    },
    
    handlePanelFocus() {
      // 通知父组件当前面板被点击，需要置顶
      this.$emit('panel-focus')
    },
    
    /**
     * 处理最小化
     */
    handleMinimize() {
      this.isMinimized = true
      // 触发最小化事件
      this.$emit('minimize', {
        panelId: this.internalPanelId,
        title: this.title,
        icon: this.icon
      })
      // 注册到 Layout
      if (this.registerMinimizedPanel) {
        this.registerMinimizedPanel({
          panelId: this.internalPanelId,
          title: this.title,
          icon: this.icon,
          restoreHandler: this.handleRestore
        })
      }
    },
    
    /**
     * 处理恢复
     */
    handleRestore() {
      this.isMinimized = false
      // 触发恢复事件
      this.$emit('restore', {
        panelId: this.internalPanelId
      })
      // 从 Layout 注销
      if (this.unregisterMinimizedPanel) {
        this.unregisterMinimizedPanel(this.internalPanelId)
      }
    },
    
    /**
     * 处理展开/收起切换
     */
    handleExpandToggle() {
      // 触发展开/收起事件，由调用方管理状态
      this.$emit('expand-toggle', !this.isExpanded)
    },

    /**
     * 最大化 / 还原
     */
    handleMaximizeToggle() {
      this.isMaximized = !this.isMaximized
      this.$emit('maximize-toggle', this.isMaximized)
    },
    
    /**
     * 更新导航栏状态
     */
    updateSidebarState() {
      const savedCollapsed = localStorage.getItem('sidebarCollapsed')
      this.sidebarCollapsed = savedCollapsed === 'true'
    },
    
    /**
     * 处理 localStorage 变化事件（跨窗口同步）
     */
    handleStorageChange(event) {
      if (event.key === 'sidebarCollapsed') {
        const oldCollapsed = this.sidebarCollapsed
        this.updateSidebarState()
        // 如果导航栏状态发生变化，且面板已初始化且用户没有手动设置 left，则更新位置
        if (oldCollapsed !== this.sidebarCollapsed && this.isInitialized && this.initialPosition.left === undefined) {
          this.updatePositionForSidebar()
        }
      }
    },
    
    /**
     * 启动导航栏状态检查轮询
     */
    startSidebarCheck() {
      // 如果已经有定时器，先清除
      this.stopSidebarCheck()
      // 每 500ms 检查一次导航栏状态
      this.sidebarCheckTimer = setInterval(() => {
        const oldCollapsed = this.sidebarCollapsed
        this.updateSidebarState()
        // 如果导航栏状态发生变化，且用户没有手动设置 left，则更新位置
        if (oldCollapsed !== this.sidebarCollapsed && this.isInitialized && this.initialPosition.left === undefined) {
          this.updatePositionForSidebar()
        }
      }, 500)
    },
    
    /**
     * 停止导航栏状态检查轮询
     */
    stopSidebarCheck() {
      if (this.sidebarCheckTimer) {
        clearInterval(this.sidebarCheckTimer)
        this.sidebarCheckTimer = null
      }
    },
    
    /**
     * 根据导航栏状态更新位置
     */
    updatePositionForSidebar() {
      const sidebarWidth = this.sidebarCollapsed ? 64 : 112
      // 只更新 x 坐标，保持 y 坐标不变
      this.position.x = sidebarWidth
    },
    
    /**
     * 获取导航栏宽度
     */
    getSidebarWidth() {
      // 检测导航栏状态
      this.updateSidebarState()
      // 收起：64px，展开：112px
      return this.sidebarCollapsed ? 64 : 112
    },
    
    /**
     * 初始化面板位置 - 将CSS定位转换为transform坐标
     */
    initializePosition() {
      // 计算初始transform位置
      let x = 0
      let y = 0
      
      // 处理水平定位
      if (this.initialPosition.left !== undefined) {
        // 如果用户明确指定了 left，使用用户的值
        x = this.initialPosition.left
      } else if (this.initialPosition.right !== undefined) {
        // right定位需要基于视窗宽度计算
        const viewportWidth = window.innerWidth
        const panelWidth = typeof this.width === 'number' ? this.width : 950
        x = viewportWidth - this.initialPosition.right - panelWidth
      } else {
        // 如果用户没有指定 left 或 right，根据导航栏状态自动计算
        x = this.getSidebarWidth()
      }
      
      // 处理垂直定位
      if (this.initialPosition.top !== undefined) {
        y = this.initialPosition.top
      } else if (this.initialPosition.bottom !== undefined) {
        // bottom定位需要基于视窗高度计算
        const viewportHeight = window.innerHeight
        // 由于height可能是auto，这里使用一个估算值
        const estimatedHeight = 500
        y = viewportHeight - this.initialPosition.bottom - estimatedHeight
      } else {
        // 默认 top 值
        y = 60
      }
      
      // 确保位置在合理范围内
      x = Math.max(0, Math.min(x, window.innerWidth - 100))
      y = Math.max(0, Math.min(y, window.innerHeight - 100))
      
      // 设置初始位置
      this.position.x = x
      this.position.y = y
      
      console.log('DraggablePanel初始化位置:', { x, y, sidebarCollapsed: this.sidebarCollapsed, initialPosition: this.initialPosition })
    },
    
    /**
     * 更新面板位置状态
     */
    updatePosition(x, y) {
      this.position.x = x
      this.position.y = y
    },
    
    /**
     * 重置面板位置到初始状态
     */
    resetPosition() {
      this.isInitialized = false
      this.initializePosition()
      this.isInitialized = true
      
      // 如果interact实例存在，重置其内部状态
      if (this.interactInstance && this.$refs.panelContainer) {
        // 清除interact.js设置的transform
        this.$refs.panelContainer.style.transform = `translate(${this.position.x}px, ${this.position.y}px)`
        
        // 重置interact.js内部位置状态
        const interactable = this.interactInstance
        if (interactable && interactable.target) {
          interactable.target.setAttribute('data-x', 0)
          interactable.target.setAttribute('data-y', 0)
        }
      }
    },
    
    /**
     * 设置面板位置（公共方法）
     * @param {number} x - X坐标
     * @param {number} y - Y坐标
     */
    setPosition(x, y) {
      // 边界检查
      const maxX = window.innerWidth - 100
      const maxY = window.innerHeight - 50
      
      const clampedX = Math.max(0, Math.min(x, maxX))
      const clampedY = Math.max(0, Math.min(y, maxY))
      
      this.updatePosition(clampedX, clampedY)
    },
    
    /**
     * 获取当前面板位置（公共方法）
     * @returns {Object} 当前位置 {x, y}
     */
    getPosition() {
      return { ...this.position }
    },
    
    /**
     * 居中显示面板（公共方法）
     */
    centerPanel() {
      const panelWidth = typeof this.width === 'number' ? this.width : 950
      const panelHeight = 500 // 估算高度
      
      const x = (window.innerWidth - panelWidth) / 2
      const y = (window.innerHeight - panelHeight) / 2
      
      this.setPosition(Math.max(0, x), Math.max(0, y))
    },
    
    initInteract() {
      const interact = require('interactjs').default
      
      if (!this.$refs.panelContainer) return
      
      // 使用interact.js，但完全接管位置管理
      this.interactInstance = interact(this.$refs.panelContainer)
        .draggable({
          // 只允许从标题栏拖拽
          allowFrom: '.draggable-panel-head',
          // 忽略导航区域
          ignoreFrom: '.draggable-panel-nav',
          // 禁用惯性
          inertia: false,
          // 位置在 move 监听里手动累加并做边界钳制；
          // 不再用 restrictRect（它与手动累加冲突，会导致首次拖动 Y 轴被吸到底部并卡死）
          listeners: {
            // 拖拽开始
            start: (event) => {
              console.log('拖拽开始:', { x: this.position.x, y: this.position.y })
            },
            
            // 拖拽中
            move: (event) => {
              if (this.isMaximized) { event.preventDefault(); return }
              // 基于当前位置累加偏移量
              const newX = this.position.x + event.dx
              const newY = this.position.y + event.dy
              
              // 边界检查
              const maxX = window.innerWidth - 100 // 至少保留100px可见
              const maxY = window.innerHeight - 50 // 至少保留50px可见
              
              const clampedX = Math.max(0, Math.min(newX, maxX))
              const clampedY = Math.max(0, Math.min(newY, maxY))
              
              // 更新位置状态
              this.updatePosition(clampedX, clampedY)
              
              // 阻止interact.js默认的transform应用
              event.preventDefault()
            },
            
            // 拖拽结束
            end: (event) => {
              console.log('拖拽结束:', { x: this.position.x, y: this.position.y })
            }
          }
        })
    }
  }
}
</script>

<style scoped>
.draggable-panel-container {
  position: fixed;
  top: 0;
  left: 0;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  /* 确保transform-origin在左上角 */
  transform-origin: 0 0;
}

/* 头部样式 */
.draggable-panel-head {
  background-color: #1e3a8a;
  color: #fff;
  padding: 12px 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: grab;
  user-select: none;
}

.draggable-panel-head:hover {
  background-color: #1e40af;
}

.draggable-panel-head:active {
  cursor: grabbing;
}

.draggable-panel-title {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  font-size: 16px;
  pointer-events: none;
}

.draggable-panel-nav {
  display: flex;
  align-items: center;
  gap: 15px;
}

.nav-item {
  padding: 0 10px;
  cursor: pointer;
  position: relative;
  white-space: nowrap;
  font-size: 14px;
  opacity: 0.8;
  transition: opacity 0.3s;
  display: flex;
  align-items: center;
  user-select: none;
}

.nav-item:hover {
  opacity: 1;
}

.nav-item.active {
  opacity: 1;
  font-weight: 500;
}

.nav-item.active::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  bottom: -12px;
  height: 2px;
  background-color: #fff;
}

.badge {
  background-color: #ff4d4f;
  color: #fff;
  font-size: 12px;
  padding: 0 5px;
  border-radius: 10px;
  min-width: 16px;
  height: 16px;
  line-height: 16px;
  text-align: center;
  margin-left: 5px;
  display: inline-block;
}

.draggable-panel-icon {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  margin-left: 10px;
}

.draggable-panel-icon:hover {
  background-color: rgba(255, 255, 255, 0.2);
}

/* 内容区域样式 */
.draggable-panel-content {
  flex: 1;
  overflow: hidden;
  position: relative;
  /* 允许内容区域文本选择 */
  user-select: text;
}

/* 拖拽时的全局样式 */
.draggable-panel-container.dragging * {
  pointer-events: none;
}

/* 响应式设计 */
@media (max-width: 1200px) {
  .draggable-panel-container {
    max-width: 95vw;
  }
}
</style>

